// Suite Actions

import isEmpty from 'lodash-es/isEmpty';
import RoomBookingApiClient from '../../../api/clients/RoomBookingApiClient';
import { getTestValueAB, TESTS_DICTIONARY } from '../../../utilities/ABTesting';
import { getTheFirstElementOfArray, isArrayEmpty } from '../../../utilities/arraysUtils';
import { OFFER_CODE_TYPE } from '../../../utilities/constants';
import { getUserIsLoggedIn } from '../Account/account.selectors';
import cartActionTypes from '../Cart/cart.types';
import packagesActionTypes from '../Packages/packages.types';
import plansActionTypes from '../Plans/plans.types';
import { gaTrackingPlanSearchErrorCode } from '../utilities/util';
import suiteActionTypes from './suite.types';

const MISMATCH = 'RATES_MISMATCH';

export const pushImpressionsAnalyticsData = eventProps => dispatch => {
  dispatch({
    type: suiteActionTypes.GTM_SUITE_IMPRESSIONS,
    eventProps
  });
};

export const pushSuiteRatesImpressionsAnalyticsData = eventProps => dispatch => {
  dispatch({
    type: suiteActionTypes.GTM_SUITE_RATES_IMPRESSIONS,
    eventProps
  });
};

/**
 * @param {Object} suite Suite to select
 * @param {Object} [eventProps] Analytics payload
 * @param {Object} [upsellRecommendationData] Upsell recommendations to carry onto the selection
 * @param {boolean} [offeredIsProperty] Private lodging, from whatever filled the room list the
 * guest chose from. Omitted, the plans slice answers, which the cart handoff never writes.
 */
export const selectSuite = (suite, eventProps, upsellRecommendationData, offeredIsProperty) => (dispatch, getState) => {
  const isProperty = offeredIsProperty ?? getState().plans.isProperty;
  const isLoggedInAtSuiteSelection = getUserIsLoggedIn(getState().account);
  dispatch({
    type: suiteActionTypes.SELECT_SUITE,
    selectedSuite: suite,
    eventProps,
    upsellRecommendationData,
    isProperty,
    isLoggedInAtSuiteSelection
  });
  // The cart handoff selects its own suite, so it claims the cart id only once hydration has
  // settled; a selection after that is the guest choosing a room the agent did not pick.
  dispatch({
    type: cartActionTypes.SET_HANDOFF_CART_ID,
    handoffCartId: null
  });
};

export const getDetailedAvailability = (requestObject, failureCallback = () => {}) => async (dispatch, getState) => {
  const skipCompareValidation = requestObject.skipCompareValidation ? requestObject.skipCompareValidation : false;
  delete requestObject.skipCompareValidation;

  dispatch({
    type: suiteActionTypes.GET_SUITE_DETAILED_AVAILABILITY_RESET
  });

  try {
    dispatch({
      type: suiteActionTypes.GET_SUITE_DETAILED_AVAILABILITY_INFO
    });

    const selectedSuite = getState().suite.selectedSuite;
    const suiteCode = selectedSuite.suiteCode;
    requestObject.suiteCode = suiteCode;
    requestObject.uffBundle = getTestValueAB(TESTS_DICTIONARY.optimizelyUFF, false) || undefined;
    const selectedRate = selectedSuite.rate;
    const offerType = getState().plans.offerType || '';

    const apiClient = new RoomBookingApiClient();
    const lodgeCode = getState().currentLodge.resortLocation;
    const response = await apiClient.getAvailabilityBySuite(lodgeCode, suiteCode, requestObject);

    if (!response.data) {
      const errorCode = await response.json();

      if (errorCode) {
        gaTrackingPlanSearchErrorCode(JSON.stringify(errorCode));
        dispatch({
          type: suiteActionTypes.GET_SUITE_DETAILED_AVAILABILITY_ERROR
        });
        dispatch({
          type: packagesActionTypes.GET_PACKAGES_AVAILABILITY_INFO_ERROR,
          errorMessage: ''
        });
        dispatch({
          type: plansActionTypes.SET_PLANS_AVAILABILITY_DETAILS_ERROR,
          error: {
            errorCode: errorCode,
            offerCode: requestObject.OfferCode
          }
        });
        failureCallback();
        return;
      }
    }

    if (response.data.suiteRates && response.data.suiteRates.length > 0) {
      response.data.suiteRates = response.data.suiteRates.map(rate => {
        rate['roomAdjustmentTax'] = rate['roomAdjustmentTax'] || 12.5;
        return rate;
      });
    }

    const uffActivities = response.data.activities;
    const isUffBundleResponse = Object.values(uffActivities ?? {}).some(value => !isEmpty(value));
    const planPackages = response.data.packageTable;

    dispatch({
      type: suiteActionTypes.GET_SUITE_DETAILED_AVAILABILITY_SUCCESS,
      suiteDetailedAvailability: [...response.data.suiteRates],
      suiteLcoPackage: response.data.lco,
      flexTripAllowed: response.data.isFlexTripAllowed ?? false,
      flexPackage: response.data.flexTripPackage,
      dayType: response.data.dayType,
      personalizationPackageRules: response.data.pr,
      personalizationResponses: response.data.rr,
      responseHasPackages: !isArrayEmpty(planPackages) || isUffBundleResponse,
      pricingKey: response.data?.pricingKey,
      taxesAndFeesDetailAvailability: response.data?.taxesAndFees,
      rateCode: getTheFirstElementOfArray(response.data?.suiteRates ?? [])?.rateCode
    });

    dispatch({ type: suiteActionTypes.SET_SUITE_PERSONALIZATION_PACKAGES });

    dispatch({
      type: packagesActionTypes.SET_PACKAGES_PLAN_INFO_SUCCESS,
      planPackages,
      uffActivities: isUffBundleResponse ? uffActivities : null
    });

    const isNotSameOffer = offerType !== OFFER_CODE_TYPE.group;
    const isRateNotSame = selectedRate
      ? selectedRate.toFixed(2) !== response.data.suiteRates[0].totalNightlyRate.toFixed(2)
      : false;

    if (!skipCompareValidation && isNotSameOffer && isRateNotSame && !requestObject.isDateChanged) {
      gaTrackingPlanSearchErrorCode(MISMATCH);
    }
  } catch (err) {
    gaTrackingPlanSearchErrorCode(JSON.stringify(err));
    dispatch({
      type: suiteActionTypes.GET_SUITE_DETAILED_AVAILABILITY_ERROR,
      suiteAvailabilityErrorMessage: `Oops! ${
        err.validationErrors && err.validationErrors.errorCode ? err.validationErrors.errorCode : err.message
      }`
    });
    dispatch({
      type: suiteActionTypes.CLEAR_SUITE_SELECTED
    });
    failureCallback();
    throw err;
  }
};

export const clearSelectedSuite = () => {
  return {
    type: suiteActionTypes.CLEAR_SUITE_SELECTED,
    selectedSuite: null,
    suiteDetailedAvailability: []
  };
};

/**
 * Post API call to get upgraded suite details
 */
export const updateSuiteRates = newSuiteRates => dispatch => {
  dispatch({
    type: suiteActionTypes.SET_SUITE_NEW_RATES,
    suiteDetailedAvailability: newSuiteRates
  });
};

export const resetAvailabilityCall = () => dispatch => {
  dispatch({
    type: suiteActionTypes.GET_SUITE_DETAILED_AVAILABILITY_RESET
  });
};
